/**
 * Zustand store for managing map maker state.
 * Handles map data, brush settings, and undo/redo functionality.
 */

import { create } from 'zustand';
import { MapData } from '../../game/map/MapData';
import { TerrainType } from '../constants/terrain';
import { MAP_TILE_WIDTH, MAP_TILE_HEIGHT } from '../constants/map';

/**
 * Operation patch for undo/redo system.
 */
export interface OperationPatch {
  positions: Array<{ x: number; y: number }>;
  previousTerrain: TerrainType[];
  newTerrain: TerrainType;
  mineNodePosition?: { x: number; y: number }; // Track mine node position if this was a mine node operation
}

/**
 * Brush size type: 1-5 for regular brushes.
 * Mine nodes use terrain type selection and always use 2x2 brush.
 */
export type BrushSize = 1 | 2 | 3 | 4 | 5;

/**
 * Map maker state interface.
 */
interface MapMakerState {
  mapData: MapData;
  selectedTerrainType: TerrainType;
  brushSize: BrushSize;
  undoStack: OperationPatch[];
  redoStack: OperationPatch[];
  maxStackSize: number;
  useColorTiles: boolean; // Toggle between color tiles and image tiles
  mapDataVersion: number; // Version counter to force re-renders when map changes

  // Actions
  setMapData: (mapData: MapData) => void;
  setTerrainType: (terrainType: TerrainType) => void;
  setBrushSize: (brushSize: BrushSize) => void;
  setUseColorTiles: (useColorTiles: boolean) => void;
  incrementMapDataVersion: () => void;
  pushOperation: (operation: OperationPatch) => void;
  undo: () => void;
  redo: () => void;
  clearRedoStack: () => void;
  clearHistory: () => void;
}

/**
 * Create initial map data.
 */
function createInitialMapData(): MapData {
  return new MapData(MAP_TILE_WIDTH, MAP_TILE_HEIGHT);
}

export const useMapMakerStore = create<MapMakerState>((set, get) => ({
  mapData: createInitialMapData(),
  selectedTerrainType: TerrainType.GRASS,
  brushSize: 1,
  undoStack: [],
  redoStack: [],
  maxStackSize: 100,
  useColorTiles: true, // Start with color tiles to avoid grass bleeding issue on initial load
  mapDataVersion: 0,

  setMapData: (mapData: MapData) => set({ mapData, mapDataVersion: get().mapDataVersion + 1 }),

  incrementMapDataVersion: () => set({ mapDataVersion: get().mapDataVersion + 1 }),

  setUseColorTiles: (useColorTiles: boolean) => set({ useColorTiles }),

  setTerrainType: (terrainType: TerrainType) => {
    // Auto-switch to 2x2 brush when mine node is selected
    if (terrainType === TerrainType.MINE_NODE) {
      set({ selectedTerrainType: terrainType, brushSize: 2 });
    } else {
      set({ selectedTerrainType: terrainType });
    }
  },

  setBrushSize: (brushSize: BrushSize) => set({ brushSize }),

  pushOperation: (operation: OperationPatch) => {
    const state = get();
    const newUndoStack = [...state.undoStack, operation];

    // Cap stack size by removing oldest operations
    const cappedStack = newUndoStack.slice(-state.maxStackSize);

    set({
      undoStack: cappedStack,
      redoStack: [], // Clear redo stack on new operation
      mapDataVersion: state.mapDataVersion + 1, // Increment version to trigger re-renders
    });
  },

  undo: () => {
    const state = get();
    if (state.undoStack.length === 0) return;

    const operation = state.undoStack[state.undoStack.length - 1];
    const map = state.mapData;

    // If this was a mine node operation, remove the mine node
    if (operation.mineNodePosition) {
      map.removeMineNode(operation.mineNodePosition.x, operation.mineNodePosition.y);
    }

    // Restore previous terrain for each position
    operation.positions.forEach((pos, index) => {
      map.setTerrain(pos.x, pos.y, operation.previousTerrain[index]);
    });

    // Move operation to redo stack
    const newUndoStack = state.undoStack.slice(0, -1);
    const newRedoStack = [...state.redoStack, operation];

    set({
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      mapDataVersion: state.mapDataVersion + 1,
    });

    // Trigger visual update for affected tiles
    const scene = (window as any).__MAP_MAKER_SCENE__;
    if (scene && scene.updateTiles) {
      scene.updateTiles(operation.positions);
    }
  },

  redo: () => {
    const state = get();
    if (state.redoStack.length === 0) return;

    const operation = state.redoStack[state.redoStack.length - 1];
    const map = state.mapData;

    // Reapply new terrain for each position
    operation.positions.forEach((pos) => {
      map.setTerrain(pos.x, pos.y, operation.newTerrain);
    });

    // If this was a mine node operation, re-add the mine node
    if (operation.mineNodePosition) {
      map.addMineNode(operation.mineNodePosition.x, operation.mineNodePosition.y);
    }

    // Move operation to undo stack
    const newRedoStack = state.redoStack.slice(0, -1);
    const newUndoStack = [...state.undoStack, operation];

    set({
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      mapDataVersion: state.mapDataVersion + 1,
    });

    // Trigger visual update for affected tiles
    const scene = (window as any).__MAP_MAKER_SCENE__;
    if (scene && scene.updateTiles) {
      scene.updateTiles(operation.positions);
    }
  },

  clearRedoStack: () => set({ redoStack: [] }),

  clearHistory: () => set({ undoStack: [], redoStack: [] }),
}));
